<admin-app-header></admin-app-header>
<app-enterprise-admin-bar></app-enterprise-admin-bar>

<div class="container-fluid admin-container">
    <!-- Page Header -->
    <div class="page-header">
        <h1>
            <i class="fa-solid fa-money-bill-transfer"></i> Accounts
            <small class="text-muted" *ngIf="enterpriseContext.currentEnterprise">
                - {{ enterpriseContext.currentEnterprise.name }}
            </small>
        </h1>
        <p class="text-muted">Overview of invoices, pro forma invoices, and payouts for this enterprise</p>
    </div>

    <!-- No Enterprise Selected -->
    <div *ngIf="!enterpriseContext.currentEnterpriseKey" class="alert alert-info">
        <i class="fa-solid fa-info-circle"></i>
        Please select an enterprise from the menu above to view accounts.
    </div>

    <!-- Content when enterprise is selected -->
    <ng-container *ngIf="enterpriseContext.currentEnterpriseKey">
        <!-- Loading -->
        <div *ngIf="loading" class="text-center py-5">
            <i class="fa-solid fa-spinner fa-spin fa-2x"></i>
            <p class="mt-2">Loading accounts data...</p>
        </div>

        <ng-container *ngIf="!loading">
            <!-- Tab Navigation -->
            <ul class="nav nav-tabs mb-4">
                <li class="nav-item">
                    <a class="nav-link" [class.active]="activeTab === 'invoices'" (click)="setTab('invoices')" href="javascript:void(0)">
                        <i class="fa-solid fa-file-invoice-dollar"></i> Invoices
                    </a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" [class.active]="activeTab === 'proforma'" (click)="setTab('proforma')" href="javascript:void(0)">
                        <i class="fa-solid fa-clock"></i> Pro Forma
                        <span class="badge bg-warning text-dark ms-1" *ngIf="proformaInvoices.length > 0">{{ proformaInvoices.length }}</span>
                    </a>
                </li>
                <li class="nav-item">
                    <a class="nav-link" [class.active]="activeTab === 'payouts'" (click)="setTab('payouts')" href="javascript:void(0)">
                        <i class="fa-solid fa-money-check-dollar"></i> Payouts
                    </a>
                </li>
            </ul>

            <!-- INVOICES TAB -->
            <div *ngIf="activeTab === 'invoices'" class="tab-content">
                <h4 class="section-title">Invoice Summary</h4>
                
                <!-- Summary Cards -->
                <div class="summary-cards">
                    <div class="summary-card summary-card--total">
                        <div class="summary-card__icon">
                            <i class="fa-solid fa-file-invoice"></i>
                        </div>
                        <div class="summary-card__content">
                            <span class="summary-card__value">{{ totalInvoicesCount }}</span>
                            <span class="summary-card__label">Total Invoices</span>
                            <span class="summary-card__subtext">{{ formatCurrency(totalInvoicesAmount) }}</span>
                        </div>
                    </div>
                    
                    <div class="summary-card" *ngFor="let summary of invoiceSummaries">
                        <div class="summary-card__header">
                            <span class="badge" [ngClass]="getStatusBadgeClass(summary.status)">{{ summary.status | titlecase }}</span>
                        </div>
                        <div class="summary-card__content">
                            <span class="summary-card__value">{{ summary.count }}</span>
                            <span class="summary-card__subtext">{{ formatCurrency(summary.totalAmount) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Recent Invoices Table -->
                <h5 class="mt-4">Recent Invoices</h5>
                <div class="table-responsive">
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Invoice #</th>
                                <th>Client</th>
                                <th>Portfolio</th>
                                <th>Status</th>
                                <th>Amount</th>
                                <th>Due Date</th>
                                <th>Paid Out</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let invoice of recentInvoices" (click)="viewInvoice(invoice.$key!)" class="clickable-row">
                                <td>{{ invoice.invoiceNumber || invoice.$key }}</td>
                                <td>{{ invoice.denormClientFirstName }} {{ invoice.denormClientLastName }}</td>
                                <td>{{ invoice.denormMerchantCompanyName }}</td>
                                <td>
                                    <span class="badge" [ngClass]="getStatusBadgeClass(invoice.status)">{{ invoice.status | titlecase }}</span>
                                </td>
                                <td>{{ formatCurrency(invoice.amountTotalDue) }}</td>
                                <td>{{ formatDate(invoice.dueDate) }}</td>
                                <td>
                                    <i class="fa-solid fa-check text-success" *ngIf="invoice.paidOut"></i>
                                    <i class="fa-solid fa-clock text-warning" *ngIf="!invoice.paidOut && invoice.status === InvoiceStatus.PAID"></i>
                                    <span class="text-muted" *ngIf="!invoice.paidOut && invoice.status !== InvoiceStatus.PAID">-</span>
                                </td>
                            </tr>
                            <tr *ngIf="recentInvoices.length === 0">
                                <td colspan="7" class="text-center text-muted py-4">No invoices found</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- PROFORMA TAB -->
            <div *ngIf="activeTab === 'proforma'" class="tab-content">
                <h4 class="section-title">Pro Forma Invoices (Awaiting Payment)</h4>
                
                <!-- Proforma Summary -->
                <div class="alert alert-warning" *ngIf="proformaInvoices.length > 0">
                    <i class="fa-solid fa-clock"></i>
                    <strong>{{ proformaInvoices.length }}</strong> invoices awaiting payment totaling
                    <strong>{{ formatCurrency(proformaTotal) }}</strong>
                </div>

                <div class="alert alert-success" *ngIf="proformaInvoices.length === 0">
                    <i class="fa-solid fa-check-circle"></i>
                    No outstanding pro forma invoices
                </div>

                <!-- Proforma Table -->
                <div class="table-responsive" *ngIf="proformaInvoices.length > 0">
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Invoice #</th>
                                <th>Client</th>
                                <th>Portfolio</th>
                                <th>Parking</th>
                                <th>Amount</th>
                                <th>Due Date</th>
                                <th>Days Overdue</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let invoice of proformaInvoices" (click)="viewInvoice(invoice.$key!)" class="clickable-row">
                                <td>{{ invoice.invoiceNumber || invoice.$key }}</td>
                                <td>{{ invoice.denormClientFirstName }} {{ invoice.denormClientLastName }}</td>
                                <td>{{ invoice.denormMerchantCompanyName }}</td>
                                <td>{{ invoice.denormParkingTitle }}</td>
                                <td><strong>{{ formatCurrency(invoice.amountTotalDue) }}</strong></td>
                                <td>{{ formatDate(invoice.dueDate) }}</td>
                                <td>
                                    <span class="badge bg-warning text-dark">Pending</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- PAYOUTS TAB -->
            <div *ngIf="activeTab === 'payouts'" class="tab-content">
                <h4 class="section-title">Payout Summary</h4>
                
                <!-- Summary Cards -->
                <div class="summary-cards">
                    <div class="summary-card summary-card--total">
                        <div class="summary-card__icon">
                            <i class="fa-solid fa-money-check-dollar"></i>
                        </div>
                        <div class="summary-card__content">
                            <span class="summary-card__value">{{ totalPayoutsCount }}</span>
                            <span class="summary-card__label">Total Payouts</span>
                            <span class="summary-card__subtext">{{ formatCurrency(totalPayoutsAmount) }}</span>
                        </div>
                    </div>
                    
                    <div class="summary-card" *ngFor="let summary of payoutSummaries">
                        <div class="summary-card__header">
                            <span class="badge" [ngClass]="getPayoutStatusBadgeClass(summary.status)">{{ summary.status | titlecase }}</span>
                        </div>
                        <div class="summary-card__content">
                            <span class="summary-card__value">{{ summary.count }}</span>
                            <span class="summary-card__subtext">{{ formatCurrency(summary.totalAmount) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Recent Payouts Table -->
                <h5 class="mt-4">Recent Payouts</h5>
                <div class="table-responsive">
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Payout #</th>
                                <th>Status</th>
                                <th>Amount</th>
                                <th>Payout Date</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let payout of recentPayouts" (click)="viewPayout(payout.$key!)" class="clickable-row">
                                <td>{{ payout.payoutNumber || payout.$key }}</td>
                                <td>
                                    <span class="badge" [ngClass]="getPayoutStatusBadgeClass(payout.status)">{{ payout.status | titlecase }}</span>
                                </td>
                                <td>{{ formatCurrency(payout.payoutAmount) }}</td>
                                <td>{{ formatDate(payout.payoutDate) }}</td>
                                <td>{{ formatDate(payout.createdAt) }}</td>
                            </tr>
                            <tr *ngIf="recentPayouts.length === 0">
                                <td colspan="5" class="text-center text-muted py-4">
                                    No payouts found. Payouts may need enterprise migration.
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Payout Batches Section -->
                <h5 class="mt-5">Recent Payout Batches</h5>
                <p class="text-muted small">Note: Payout batches are shown across all enterprises until migration is complete.</p>
                <div class="table-responsive">
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Batch #</th>
                                <th>Status</th>
                                <th>Payouts</th>
                                <th>Total Amount</th>
                                <th>Payout Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let batch of payoutBatches" (click)="viewPayoutBatch(batch.$key!)" class="clickable-row">
                                <td>{{ batch.payoutBatchNumber || batch.$key }}</td>
                                <td>
                                    <span class="badge" [ngClass]="getPayoutBatchStatusBadgeClass(batch.status)">{{ batch.status | titlecase }}</span>
                                </td>
                                <td>{{ batch.payoutRefs?.length || 0 }}</td>
                                <td>{{ formatCurrency(batch.payoutAmount) }}</td>
                                <td>{{ formatDate(batch.payoutDate) }}</td>
                            </tr>
                            <tr *ngIf="payoutBatches.length === 0">
                                <td colspan="5" class="text-center text-muted py-4">No payout batches found</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </ng-container>
    </ng-container>
</div>

